import { useEffect } from 'react';

import { RootState } from '@iwProvider/configureStore';
import { H1 } from '@ui';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { getFormValues, isValid, reset, submit } from 'redux-form';

import AccountDetails from './accountDetails/AccountDetails';
import CompanyDetails from './companyDetails/CompanyDetails';
import { SUBMIT_COMPANY_DETAILS } from './companyDetails/CompanyDetails.actions';
import PaymentDetails from './paymentDetails/PaymentDetails';
import People from './people/People';
import {
    ACTIVATE_PROFILE,
    CANCEL_PROMPT,
    LEAVE_PROFILE,
    OPEN_PROMPT
} from './Profile.actions';
import { PromptSave } from './promptSave';
import { useTrackPageView } from '../../tracking/pageView';
import MainContentPending from '../pageStructure/mainContentPending/MainContentPending';

const mapStateToProps = (state: RootState) => ({
    ...state.profile,
    companyFormValues: getFormValues('CompanyDetails')(state),
    isCompanyFormValid: isValid('CompanyDetails')(state)
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    activate: () => dispatch({ type: ACTIVATE_PROFILE }),
    leave: () => dispatch({ type: LEAVE_PROFILE }),
    promptSave: () => dispatch({ type: OPEN_PROMPT }),
    cancelPrompt: () => dispatch({ type: CANCEL_PROMPT }),
    // @ts-expect-error
    resetForm: () => dispatch(reset('CompanyDetails')),
    // @ts-expect-error
    validateForm: () => dispatch(submit('CompanyDetails')),
    submitForm: (companyDetails: object) =>
        dispatch({ type: SUBMIT_COMPANY_DETAILS, companyDetails })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const Profile = ({
    activate,
    leave,
    resetForm,
    cancelPrompt,
    validateForm,
    submitForm,
    companyFormValues,
    isCompanyFormValid,
    ready,
    openPrompt,
    // @ts-expect-error
    submitCompanyDetailsLoading,
    promptSave
}: PropsFromRedux) => {
    useTrackPageView({ name: 'Profile' });

    useEffect(() => {
        activate();

        return () => {
            leave();
        };
    }, [activate, leave]);

    const handleCancelPrompt = () => {
        resetForm();
        setTimeout(() => {
            cancelPrompt();
        }, 0);
    };

    const submitPrompt = () => {
        validateForm();

        if (isCompanyFormValid) {
            submitForm(companyFormValues);
        }
    };

    if (!ready) return <MainContentPending data-testid="profile-loading" />;

    return (
        <div className="mx-auto max-w-5xl space-y-2xl px-s py-xl">
            <H1>
                <FormattedMessage
                    id="_.your_company.title"
                    defaultMessage="Profile"
                />
            </H1>

            <People />

            <PaymentDetails />

            {/* @ts-expect-error */}
            <CompanyDetails onChange={promptSave} />

            <AccountDetails />

            <PromptSave
                openPrompt={openPrompt}
                handleSubmitPrompt={submitPrompt}
                handleCancelPrompt={handleCancelPrompt}
                loading={submitCompanyDetailsLoading}
            />
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(Profile);
